/* A single native Shopify form powers the hero and floating purchase pill. */
if (!customElements.get('il-product-page')) {
  customElements.define('il-product-page', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.form = this.querySelector('.il-pdp__form');
      this.variantSelect = this.form?.querySelector('[name="id"]');
      if (!this.form || !this.variantSelect) return;
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.quantity = this.form.querySelector('[name="quantity"]');
      this.buyButtons = [...this.querySelectorAll('[data-main-buy],[data-sticky-buy]')];
      this.bundleForms = [...this.querySelectorAll('[data-bundle-form]')];
      this.bundleButtons = [...this.querySelectorAll('[data-bundle-buy]')];
      this.bundleVariants = JSON.parse(this.querySelector('[data-bundle-variants]')?.textContent || '[]');
      this.bundleForms.forEach(form => {
        form.addEventListener('submit', event => this.addToCart(event, form), options);
        form.querySelector('[data-bundle-option]').addEventListener('change', event => {
          this.bundleForms.forEach(other => { other.querySelector('[data-bundle-option]').value = event.target.value; });
          this.updateBundle();
        }, options);
      });
      this.sticky = this.querySelector('.il-pdp__sticky');
      this.message = this.querySelector('[data-cart-message]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.variantSelect.addEventListener('change', () => this.updateVariant(true), options);
      this.form.addEventListener('submit', event => this.addToCart(event), options);
      this.addEventListener('click', event => {
        const step = event.target.closest('[data-quantity-step]');
        if (step) {
          const increment = Number(this.quantity.step) || 1;
          const min = Number(this.quantity.min) || 1;
          const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
          this.quantity.value = Math.max(min, Math.min(max, Number(this.quantity.value || min) + Number(step.dataset.quantityStep) * increment));
          this.quantity.dispatchEvent(new Event('change', { bubbles:true }));
        }
        const zoom = event.target.closest('[data-zoom]');
        if (zoom) {
          this.lastImageTrigger = zoom;
          const dialog = this.querySelector('dialog');
          dialog.querySelector('img').src = zoom.dataset.zoom;
          dialog.querySelector('[data-original-image]').href = zoom.dataset.zoom;
          dialog.showModal();
        }
        if (event.target.closest('[data-close-dialog]')) this.querySelector('dialog').close();
        if (event.target === this.querySelector('dialog')) this.querySelector('dialog').close();
        const arrow = event.target.closest('[data-slide]');
        if (arrow) {
          const track = this.querySelector('.il-pdp__ingredient-track');
          track.scrollBy({left:Number(arrow.dataset.slide) * track.clientWidth * .85, behavior:this.motion.matches ? 'instant':'smooth'});
        }
      }, options);
      this.querySelector('dialog').addEventListener('close', () => this.lastImageTrigger?.focus(), options);
      this.stickyObserver = new IntersectionObserver(entries => {
        const rect = entries[0].boundingClientRect;
        this.sticky.hidden = entries[0].isIntersecting || rect.top > 0;
      }, {threshold:0});
      this.stickyObserver.observe(this.querySelector('[data-main-buy]'));
      this.videoObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.motion.matches) entry.target.play().catch(() => {});
          else entry.target.pause();
        }
      }, {threshold:.15});
      this.querySelectorAll('video:not(.il-pdp__experience-video):not(.il-capsule__video)').forEach(video => this.videoObserver.observe(video));
      this.motion.addEventListener('change', () => {
        this.querySelectorAll('video:not(.il-pdp__experience-video):not(.il-capsule__video)').forEach(video => {
          if (this.motion.matches) video.pause();
          else {this.videoObserver.unobserve(video);this.videoObserver.observe(video);}
        });
      }, options);
      const observePreview = () => {
        const bar = document.getElementById('PBarNextFrame');
        if (!bar || this.previewObserver) return;
        const update = () => this.style.setProperty('--pdp-preview-height', `${Math.min(64, bar.getBoundingClientRect().height)}px`);
        this.previewObserver = new ResizeObserver(update);
        this.previewObserver.observe(bar);
        update();
      };
      observePreview();
      window.addEventListener('load', observePreview, options);
      this.previewTimer = window.setTimeout(observePreview, 1500);
      this.updateVariant(false);
    }
    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.previewTimer);
      this.previewObserver?.disconnect();
      this.stickyObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.querySelectorAll('video').forEach(video => video.pause());
    }
    updateVariant(updateUrl) {
      this.variant = this.variants.find(v => String(v.id) === this.variantSelect.value);
      const v = this.variant;
      if (!v) return;
      this.querySelector('[data-price]').textContent = v.price;
      this.querySelector('[data-sticky-price]').textContent = v.price;
      const compare = this.querySelector('[data-compare-price]');
      compare.hidden = !v.compare;
      compare.textContent = v.compare || '';
      this.quantity.min = v.min;
      this.quantity.step = v.step;
      if (v.max) this.quantity.max = v.max; else this.quantity.removeAttribute('max');
      this.quantity.value = Math.max(v.min,Math.min(v.max || Infinity,Number(this.quantity.value) || v.min));
      this.buyButtons.forEach(button => {
        button.disabled = !v.available || !!this.busy;
        button.querySelector('[data-buy-label]').textContent = v.available ? (button.dataset.labelAvailable || window.themeVariables?.strings?.addToCart || 'Add to cart') : (window.themeVariables?.strings?.soldOut || 'Sold out');
      });
      if (updateUrl) {
        const url = new URL(location.href);
        url.searchParams.set('variant',v.id);
        history.replaceState({},'',url);
      }
      this.form.dispatchEvent(new CustomEvent('variant:changed',{bubbles:true,detail:{variant:v}}));
      this.updateBundle();
    }
    updateBundle() {
      const v = this.variant;
      if (!v || !this.bundleForms.length) return;
      this.bundleVariant = this.bundleVariants.find(extra => String(extra.id) === this.bundleForms[0].querySelector('[data-bundle-option]').value);
      const extra = this.bundleVariant;
      if (!extra) return;
      this.bundleForms.forEach(form => {
        form.querySelector('[data-bundle-main-id]').value = v.id;
        form.querySelector('[data-bundle-main-quantity]').value = v.min;
        form.querySelector('[data-bundle-extra-quantity]').value = extra.quantity;
        form.querySelector('[data-bundle-main-count]').textContent = v.min;
        form.querySelector('[data-bundle-extra-count]').textContent = extra.quantity;
        form.querySelector('[data-bundle-main-price]').textContent = v.price;
        form.querySelector('[data-bundle-extra-price]').textContent = extra.price;
        form.querySelector('[data-bundle-main-option]').textContent = v.title === 'Default Title' ? '' : v.title;
        form.querySelector('[data-bundle-total]').textContent = extra.totals[v.id];
        const button = form.querySelector('[data-bundle-buy]');
        button.disabled = !v.available || !extra.available || !!this.busy;
        button.querySelector('[data-buy-label]').textContent = v.available && extra.available ? (button.dataset?.bundleLabel || 'Add both to cart') : 'Bundle unavailable';
      });
    }
    async addToCart(event, bundleForm = null) {
      event.preventDefault();
      const activeForm = bundleForm || this.form;
      if (this.busy || !this.variant?.available || (bundleForm && !this.bundleVariant?.available) || !activeForm.reportValidity()) return;
      this.busy = true;
      const message = bundleForm ? bundleForm.querySelector('[data-bundle-message]') : this.message;
      this.message.textContent = '';
      this.querySelectorAll('[data-bundle-message]').forEach(node => {node.textContent = '';});
      const buttons = [...this.buyButtons, ...this.bundleButtons];
      buttons.forEach(b => {b.disabled = true;b.setAttribute('aria-busy','true');});
      (bundleForm ? [bundleForm.querySelector('[data-bundle-buy]')] : this.buyButtons).forEach(b => {b.querySelector('[data-buy-label]').textContent='Adding…';});
      const root = window.Shopify?.routes?.root || '/';
      try {
        let data;
        const headers = {'X-Requested-With':'XMLHttpRequest'};
        if (bundleForm) {
          headers['Content-Type'] = 'application/json';
          data = JSON.stringify({items:[{id:this.variant.id,quantity:this.variant.min},{id:this.bundleVariant.id,quantity:this.bundleVariant.quantity}],sections:['mini-cart'],sections_url:location.pathname});
        } else {
          data = new FormData(this.form);
          data.set('sections','mini-cart');
          data.set('sections_url',location.pathname);
        }
        const response = await fetch(`${root}cart/add.js`,{method:'POST',body:data,headers,signal:this.abort.signal});
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'This item could not be added. Please check the selected option and quantity.');
        message.textContent = bundleForm ? 'Both products added to your cart.' : 'Added to your cart.';
        for (const item of result.items || [result]) activeForm.dispatchEvent(new CustomEvent('variant:added',{bubbles:true,detail:{variant:item}}));
        if (result.sections?.['mini-cart']) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh',{bubbles:true,detail:{cart:result,openMiniCart:window.themeVariables?.settings?.cartType === 'drawer'}}));
        try {
          const cart = await (await fetch(`${root}cart.js`,{signal:this.abort.signal})).json();
          document.documentElement.dispatchEvent(new CustomEvent('cart:updated',{bubbles:true,detail:{cart}}));
        } catch (error) { /* The item was added; a delayed count refresh must not invite a duplicate submission. */ }
        if (window.themeVariables?.settings?.cartType === 'page') location.assign(`${root}cart`);
      } catch (error) {
        if (error.name !== 'AbortError') {
          message.textContent = error.message || 'Unable to add this item. Please try again.';
          if (!bundleForm && !this.sticky.hidden) message.scrollIntoView({block:'center',behavior:this.motion.matches ? 'instant':'smooth'});
        }
      } finally {
        this.busy = false;
        buttons.forEach(b => b.removeAttribute('aria-busy'));
        if (this.isConnected) this.updateVariant(false);
      }
    }
  });
}
